import { comparePanelStyles, getPanelStyleFromEntity, getPanelsInDoorFrameSortedFromTopToBottom } from 'components/Panels/helpers/panels';
import { PANELS_THAT_CAN_NOT_BE_CUT } from 'products/panels';
import type { IPanelStyle } from 'redux-store/configuration/configuration.store';
import { PANELS_PER_DOOR_FRAME } from 'utils/constants';
import { nonNullable } from 'utils/nonNullable';

import type { PanelComponent } from './Components/PanelComponent';
import type { RailComponent } from './Components/RailComponent';
import { RailTypeEnum } from './Components/RailTypeEnum';
import { SkyttaComponentIds } from './Components/SkyttaComponentIds';
import type { SlidingDoorFrameComponent } from './Components/SlidingDoorFrameComponent';
import type { RoomDimensionOptionsComponent } from './Components/UserChoicesComponents/RoomDimensionOptionsComponent';
import { DefaultRoomSettings } from './helpers/room';
import {
  ComponentIds,
  type Core,
  type Entity,
  type ProductComponent,
  type RoomGeometryComponent,
  type WorldTransformComponent
} from '@inter-ikea-core/core';

/**
 * @param core
 * @returns The selected entity if one is selected
 */
export const getSelectedEntity = (core: Core) => {
  return core.services.sceneManager.entities.find((entity: Entity) => entity.hasComponent(ComponentIds.SELECTED_COMPONENT_ID)) ?? null;
};

/**
 * @param core
 * @returns The selected entity if more is selected
 */
export const getSelectedEntities = (core: Core) => {
  const selected: Entity[] = [];

  core.services.sceneManager.entities.forEach((entity: Entity) => {
    if (entity.hasComponent(ComponentIds.SELECTED_COMPONENT_ID)) {
      selected.push(entity);
    }
  });

  return selected;
};

/**
 * Gets all panels in the scene
 * @param core
 * @returns
 */
export const getPanelsInScene = (core: Core) => {
  return core.services.sceneManager.entities.filter((entity: Entity) => entity.hasComponent(SkyttaComponentIds.PanelComponentId));
};

/**
 * Gets all rails in scene (both top and bottom)
 * @param core
 * @returns
 */
export const getRailsInScene = (core: Core) => {
  return core.services.sceneManager.entities.filter((entity: Entity) => entity.hasComponent(SkyttaComponentIds.RailComponentId));
};

export const getBottomRailsInScene = (core: Core) => {
  return core.services.sceneManager.entities.filter(
    (it) => it.getComponent<RailComponent>(SkyttaComponentIds.RailComponentId)?.railType === RailTypeEnum.BottomRail
  );
};

export const getTopRailsInScene = (core: Core) => {
  return core.services.sceneManager.entities.filter(
    (it) => it.getComponent<RailComponent>(SkyttaComponentIds.RailComponentId)?.railType === RailTypeEnum.TopRail
  );
};

export const getStopBarsInScene = (core: Core) => {
  return core.services.sceneManager.entities.filter((it) => it.hasComponent(SkyttaComponentIds.StopBarComponentId));
};

export const getChrFramesInScene = (core: Core) => {
  return core.services.sceneManager.entities.filter((it) => it.hasComponent(SkyttaComponentIds.CeilingHeightReducerFrameComponentId));
};

/**
 * Gets all door frames in scene
 * @param core
 * @returns
 */
export const getDoorsFramesInScene = (core: Core) => {
  return core.services.sceneManager.entities.filter((entity: Entity) => entity.hasComponent(SkyttaComponentIds.SlidingDoorFrameComponentId));
};

/**
 * Gets all door frames in scene sorted on lowest X to highest
 * @param core
 * @returns
 */
export const getDoorsFramesInSceneSortedLeftToRight = (core: Core) => {
  const sortDoorFrames = (a: Entity, b: Entity) => {
    return (
      (a.getComponent<WorldTransformComponent>(ComponentIds.WORLD_TRANSFORM_COMPONENT_ID)?.transform?.position?.X || 0) -
      (b.getComponent<WorldTransformComponent>(ComponentIds.WORLD_TRANSFORM_COMPONENT_ID)?.transform?.position?.X || 0)
    );
  };

  return getDoorsFramesInScene(core).sort(sortDoorFrames);
};

const sortDoorsByRailTrack = (a: Entity, b: Entity) => {
  const aTrack = a.getComponentUnsafe<SlidingDoorFrameComponent>(SkyttaComponentIds.SlidingDoorFrameComponentId)?.railTrack;

  const bTrack = b.getComponentUnsafe<SlidingDoorFrameComponent>(SkyttaComponentIds.SlidingDoorFrameComponentId)?.railTrack;

  if (aTrack === undefined || bTrack === undefined) {
    throw new Error('No rail track set on door');
  }

  return aTrack - bTrack;
};

const groupByRailTrack = (railTrackGroups: Entity[][], door: Entity) => {
  const railTrack = door.getComponent<SlidingDoorFrameComponent>(SkyttaComponentIds.SlidingDoorFrameComponentId)?.railTrack;

  if (railTrack === undefined) {
    return railTrackGroups;
  }

  if (railTrackGroups[railTrack]) {
    railTrackGroups[railTrack].push(door);
  } else {
    if (railTrackGroups.length && !railTrackGroups[railTrack - 1]) {
      railTrackGroups.push([]);
    }
    railTrackGroups.push([door]);
  }

  return railTrackGroups;
};

export const getDoorsByRailTrack = (core: Core) => {
  return getDoorsFramesInSceneSortedLeftToRight(core).sort(sortDoorsByRailTrack).reduce(groupByRailTrack, []);
};

/**
 * Putting the doors in correct order of placement
 * First we put one door for each track from left to right until we reach the end of tracks,
 * then put them one by one in reversed order.
 * This will make sure the doors in the middle will end up next to each other.
 *
 * Special cases:
 * - If there are 4 doors we want the middle doors to be placed on the front track, all other will start with the one to the left being in front.
 * - If there are more than 3 doors on each track (ie 5 or 6 doors on a 2 tracks) we alternate them instead so no door end up next to each other.
 */
export const getDoorsInOrderOfPlacement = (doorsByRailTrack: Entity[][] | number[][]) => {
  const doors: Array<Entity | number> = [];

  // Get the maximum number of doors on each track
  const maxDoorsOnTrack = Math.max(...doorsByRailTrack.map((subArr) => subArr.length));

  // If there are more than 3 doors on any track we should alternate the doors instead
  const shouldAlternateDoors = maxDoorsOnTrack > 2;

  const totalNrOfDoors = doorsByRailTrack.flat().length;

  for (let doorIndex = 0; doorIndex < maxDoorsOnTrack; doorIndex++) {
    // Start by putting even doors to the first track, unless there are 4 doors
    const isEvenDoor = doorIndex % 2 === 0;
    const ascendingOrder = totalNrOfDoors === 4 ? isEvenDoor : !isEvenDoor;

    if (ascendingOrder && !shouldAlternateDoors) {
      // Ascending order
      for (let trackIndex = 0; trackIndex < doorsByRailTrack.length; trackIndex++) {
        const door = doorsByRailTrack[trackIndex][doorIndex];
        if (door !== undefined) {
          doors.push(door);
        }
      }
    } else {
      // Descending direction
      for (let trackIndex = doorsByRailTrack.length - 1; trackIndex >= 0; trackIndex--) {
        const door = doorsByRailTrack[trackIndex][doorIndex];
        if (door !== undefined) {
          doors.push(door);
        }
      }
    }
  }

  return doors;
};

/**
 * Gets the room dimensions along X-axis
 * @param core
 * @returns
 */
export const getRoomWidth = (core: Core): number => {
  const room = core.services.sceneManager.entities.find((it) => it.hasComponent(ComponentIds.ROOM_GEOMETRY_COMPONENT_ID));
  const width =
    room?.getComponent<RoomGeometryComponent>(ComponentIds.ROOM_GEOMETRY_COMPONENT_ID)?.dimensions.size.X ?? DefaultRoomSettings.ROOM_WIDTH_M * 1000;
  return width;
};

export const getRoomHeight = (core: Core): number => {
  const room = core.services.sceneManager.entities.find((it) => it.hasComponent(ComponentIds.ROOM_GEOMETRY_COMPONENT_ID));
  const height =
    room?.getComponent<RoomGeometryComponent>(ComponentIds.ROOM_GEOMETRY_COMPONENT_ID)?.dimensions.size.Y ?? DefaultRoomSettings.ROOM_HEIGHT_M * 1000;
  return height;
};

export const getUserRoomHeight = (core: Core): number => {
  const roomDimensions = core.services.sceneManager.entities.find((it) => it.hasComponent(SkyttaComponentIds.RoomDimensionOptionsComponentId));

  const height =
    roomDimensions?.getComponent<RoomDimensionOptionsComponent>(SkyttaComponentIds.RoomDimensionOptionsComponentId)?.height ??
    DefaultRoomSettings.ROOM_HEIGHT_M * 1000;
  return height;
};

export const getUniquePanelStyles = (panels: IPanelStyle[]) => {
  return panels.reduce((acc: IPanelStyle[], panel) => {
    if (!acc.find((i) => comparePanelStyles(i, panel))) {
      acc.push(panel);
    }

    return acc;
  }, []);
};

export const getPanelStyles = (core: Core): IPanelStyle[][] => {
  const allDoors = getDoorsInOrderOfPlacement(getDoorsByRailTrack(core)) as Entity[];
  return allDoors.map((doorFrame) =>
    getPanelsInDoorFrameSortedFromTopToBottom(doorFrame)
      .map((panel) => getPanelStyleFromEntity(panel))
      .filter(nonNullable)
  );
};

export const isSkyttaEntityWithViewerObject = (entity: Entity) => {
  return (
    entity.hasComponent(ComponentIds.VIEWER_OBJECT_COMPONENT_ID) &&
    !entity.hasComponent(SkyttaComponentIds.StorageBehindComponentId) &&
    !entity.hasComponent(ComponentIds.PROPPING_COMPONENT_ID)
  );
};

export const isSelectedPanelsPossibleToCut = (core: Core) => {
  const panelStyleIndexes = core.services.sceneManager.entities
    .filter((e) => {
      // Only look at the top panel
      return e.getComponent<PanelComponent>(SkyttaComponentIds.PanelComponentId)?.verticalPositionInDoorFrame === PANELS_PER_DOOR_FRAME;
    })
    .map((e) => {
      return e.getComponent<PanelComponent>(SkyttaComponentIds.PanelComponentId)?.styleIndex;
    });

  return !panelStyleIndexes.some((panelStyleIndex) => PANELS_THAT_CAN_NOT_BE_CUT.includes(panelStyleIndex ?? -1));
};

export const haveUnusedPanels = (core: Core) => {
  const allPanels = getPanelsInScene(core);

  // Count nr productRefs in the scene
  const prodRefs = allPanels.reduce((countMap: Record<string, number>, panel: Entity) => {
    const productRef = panel.getComponent<ProductComponent>(ComponentIds.PRODUCT_COMPONENT_ID)?.productRef;

    if (productRef) {
      countMap[productRef] = (countMap[productRef] || 0) + 1;
    }

    return countMap;
  }, {});

  // Check if any have more or less than expected number of panels in one door.
  return Object.values(prodRefs).some((nrOfPanels) => nrOfPanels % PANELS_PER_DOOR_FRAME !== 0);
};
